/**
 * Invoice Document Type Definitions
 * 
 * Defines the different types of documents that can be attached to an invoice
 * Each invoice can have:
 * - Finalised Invoice (the main invoice document)
 * - Delivery Order (DO)
 * - Purchase Order (PO) 
 * - Rental Terms (RT)
 */

export const INVOICE_DOCUMENT_TYPES = {
  INVOICE: {
    key: 'invoice',
    label: 'Invoice (finalised)',
    suffix: '',
    description: 'finalised invoice document',
    required: true,
    icon: '📄',
    color: 'blue'
  },
  DELIVERY_ORDER: {
    key: 'deliveryOrder',
    label: 'Delivery Order (DO)',
    suffix: '_DO',
    description: 'Delivery order document',
    required: false,
    icon: '📦',
    color: 'green'
  },
  PURCHASE_ORDER: {
    key: 'purchaseOrder',
    label: 'Purchase Order (PO)',
    suffix: '_PO',
    description: 'Purchase order from client',
    required: false,
    icon: '🛒',
    color: 'orange',
    allowCustomNumber: true // Allow client PO number input
  },
  RENTAL_TERMS: {
    key: 'rentalTerms',
    label: 'Rental Terms (Signed)',
    suffix: '_RT',
    description: 'Signed rental terms and conditions',
    required: false,
    icon: '📋',
    color: 'purple'
  }
};

/**
 * Get document type configuration by key
 * @param {string} key - Document type key
 * @returns {Object|null} Document type configuration
 */
export const getDocumentType = (key) => {
  return Object.values(INVOICE_DOCUMENT_TYPES).find(type => type.key === key) || null;
};

/**
 * Get all document types as array
 * @returns {Array} Array of document type configurations
 */
export const getAllDocumentTypes = () => {
  return Object.values(INVOICE_DOCUMENT_TYPES);
};

/**
 * Generate filename for document
 * @param {string} invoiceNumber - Invoice number (e.g., BSIN-122023-134)
 * @param {string} documentTypeKey - Document type key
 * @returns {string} Filename with proper suffix
 */
export const generateDocumentFilename = (invoiceNumber, documentTypeKey) => {
  const docType = getDocumentType(documentTypeKey);
  if (!docType) {
    throw new Error(`Invalid document type: ${documentTypeKey}`);
  }
  return `${invoiceNumber}${docType.suffix}.pdf`;
};

/**
 * Generate storage path for document
 * @param {string} invoiceNumber - Invoice number
 * @param {string} documentTypeKey - Document type key
 * @returns {string} Storage path
 */
export const generateStoragePath = (invoiceNumber, documentTypeKey) => {
  const filename = generateDocumentFilename(invoiceNumber, documentTypeKey);
  return `invoices/${invoiceNumber}/${filename}`;
};

/**
 * Get document status badge color
 * @param {boolean} isUploaded - Whether document is uploaded
 * @param {boolean} isRequired - Whether document is required
 * @returns {string} Ant Design badge color
 */
export const getDocumentStatusColor = (isUploaded, isRequired) => {
  if (isUploaded) {
    return 'success';
  }
  if (isRequired) {
    return 'error';
  }
  return 'default';
};

/**
 * Get document status text
 * @param {boolean} isUploaded - Whether document is uploaded
 * @param {boolean} isRequired - Whether document is required
 * @returns {string} Status text
 */
export const getDocumentStatusText = (isUploaded, isRequired) => {
  if (isUploaded) {
    return 'Uploaded';
  }
  if (isRequired) {
    return 'Required - Not Uploaded';
  }
  return 'Not Uploaded';
};
